import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Settings01Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { Skeleton } from '@/components/Skeleton';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { IconButton } from '@/design-system';
import { usePlaygroundSettings } from '@/hooks/useSwr';

import { PlaygroundIcon } from '../../PlaygroundHome/PlaygroundButtons';
import {
  useDeep,
  usePlaygroundStore,
} from '../../PlaygroundHome/hooks/usePlaygroundStore';
import { ManifestPreview } from '..//manifest/Manifest';
import {
  EvaluatorPromptSettingsModal,
  PromptSettingsModal,
} from './PromptSettingsModal';

const PromptSettings = ({
  index,
  storedConfigIdToLoad,
  setStoredConfigIdToLoad,
}: {
  index: number;
  storedConfigIdToLoad: string | undefined;
  setStoredConfigIdToLoad: (id: string | undefined) => void;
}) => {
  const manifest = usePlaygroundStore(
    useDeep((state) => state.prompts[index].manifest)
  );

  return (
    <PromptSettingsDisplay
      manifest={manifest}
      variant="playground"
      index={index}
      setStoredConfigIdToLoad={setStoredConfigIdToLoad}
      storedConfigIdToLoad={storedConfigIdToLoad}
    />
  );
};

type PromptSettingsDisplayProps = {
  manifest: SerializedConstructor;
  storedConfigIdToLoad: string | undefined;
  setStoredConfigIdToLoad: (id: string | undefined) => void;
} & (
  | {
      variant: 'playground';
      index: number;
    }
  | {
      variant: 'evaluator';
      setManifest: (manifest: SerializedConstructor) => void;
    }
);

const PromptSettingsDisplay = (props: PromptSettingsDisplayProps) => {
  const { manifest, storedConfigIdToLoad, setStoredConfigIdToLoad } = props;
  const manifestLast = manifest.kwargs.last;

  const {
    data: storedPlaygroundSettings,
    isLoading: isLoadingPlaygroundSettings,
  } = usePlaygroundSettings();

  const config = storedPlaygroundSettings?.find(
    (config) => config.id === storedConfigIdToLoad
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const configLabelAndButton = (
    <div className="flex min-w-0 flex-row items-center gap-1">
      <PlaygroundIcon className="min-w-0 shrink cursor-default whitespace-nowrap border-none bg-tertiary px-2 py-1 hover:bg-tertiary">
        {config ? (
          <div className="min-w-0 max-w-[200px] shrink">
            <TruncatedTextWithTooltip
              text={config?.name}
              className="block max-w-full font-semibold"
            />
          </div>
        ) : (
          <div className="min-w-0 max-w-[200px] shrink overflow-hidden">
            <ManifestPreview variant="line" options={{}} value={manifestLast} />
          </div>
        )}
      </PlaygroundIcon>
      <IconButton
        variant="outlined"
        color="secondary"
        onClick={() => setIsSettingsOpen(true)}
        data-testid="prompt-settings-button"
        icon={Settings01Icon}
        label="Prompt settings"
      />
    </div>
  );

  // if there's a default config we need to load
  const configDisplay =
    isLoadingPlaygroundSettings && storedConfigIdToLoad ? (
      <Skeleton className="h-5 w-[150px]" />
    ) : (
      <div className="min-w-0">{configLabelAndButton}</div>
    );

  if (props.variant === 'playground') {
    const { index } = props;
    return (
      <>
        {configDisplay}
        {isSettingsOpen && (
          <PromptSettingsModal
            isSettingsOpen={isSettingsOpen}
            setIsSettingsOpen={setIsSettingsOpen}
            index={index}
            currentSavedConfigId={config?.id}
            setCurrentSavedConfigId={setStoredConfigIdToLoad}
          />
        )}
      </>
    );
  }

  if (props.variant === 'evaluator') {
    return (
      <>
        {configDisplay}
        {isSettingsOpen && (
          <EvaluatorPromptSettingsModal
            isSettingsOpen={isSettingsOpen}
            setIsSettingsOpen={setIsSettingsOpen}
            manifest={manifest}
            setManifest={props.setManifest}
            currentSavedConfigId={storedConfigIdToLoad}
            setCurrentSavedConfigId={setStoredConfigIdToLoad}
          />
        )}
      </>
    );
  }
};

export { PromptSettings, PromptSettingsDisplay };
